import { Link } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef } from 'react';
import { featuredComics, hotComics, newComics, categories, comics } from '@/mocks/comics';
import ComicCard from '@/components/feature/ComicCard';

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const autoPlayRef = useRef<ReturnType<typeof setInterval>>();

  const heroComics = featuredComics.slice(0, 4);

  const stopAutoPlay = useCallback(() => {
    setIsAutoPlaying(false);
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
  }, []);

  const startAutoPlay = useCallback(() => {
    setIsAutoPlaying(true);
  }, []);

  useEffect(() => {
    if (!isAutoPlaying) return;
    autoPlayRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroComics.length);
    }, 5000);
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [isAutoPlaying, heroComics.length]);

  const latestComics = [...comics]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 8);

  return (
    <main>
      {/* Hero Section */}
      <section className="relative h-[480px] md:h-[580px] flex items-center overflow-hidden">
        {/* Background Images */}
        {heroComics.map((comic, index) => (
          <div
            key={comic.id}
            className={`absolute inset-0 transition-opacity duration-700 ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`}
          >
            <img
              src={comic.coverImage}
              alt=""
              className="w-full h-full object-cover object-top blur-sm scale-110"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/60"></div>

        {/* Hero Content */}
        <div className="relative z-10 w-full px-4 md:px-6">
          <div className="max-w-6xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-bold font-heading text-background-50 mb-4">
              免费漫画网 - 正版韩漫大全
            </h1>
            <p className="text-base md:text-lg text-background-200/90 max-w-2xl mx-auto mb-8 leading-relaxed">
              免费韩漫下拉式阅读平台，内容合法、画质优越。<strong>热门韩漫、恋爱漫画、奇幻漫画</strong>一网打尽，随时随地畅享阅读乐趣。
            </p>

            {/* Hero CTA */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/all"
                className="px-8 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                开始阅读
              </Link>
              <Link
                to="/category/all?sort=rating"
                className="px-8 py-3 rounded-full bg-background-50/20 backdrop-blur-sm text-background-50 font-semibold text-sm border border-background-50/30 hover:bg-background-50/30 transition-colors whitespace-nowrap cursor-pointer"
              >
                热门排行榜
              </Link>
            </div>

            {/* Slide Indicators */}
            <div className="flex items-center justify-center gap-2 mt-8">
              {heroComics.map((_, index) => (
                <button
                  key={index}
                  onClick={() => { setCurrentSlide(index); stopAutoPlay(); }}
                  onMouseEnter={stopAutoPlay}
                  onMouseLeave={startAutoPlay}
                  className={`w-2 h-2 rounded-full transition-all cursor-pointer ${index === currentSlide ? 'w-6 bg-primary-500' : 'bg-background-50/40 hover:bg-background-50/60'}`}
                  aria-label={`幻灯片 ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-background-50 border-b border-background-200">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { label: '漫画总数', value: '10,000+', icon: 'ri-book-open-line' },
              { label: '正版授权', value: '100%', icon: 'ri-shield-check-line' },
              { label: '月活用户', value: '500万+', icon: 'ri-user-heart-line' },
              { label: '日更新量', value: '200+', icon: 'ri-refresh-line' },
            ].map((stat) => (
              <div key={stat.label} className="py-3">
                <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2 text-primary-500">
                  <i className={`${stat.icon} text-xl`}></i>
                </div>
                <div className="text-xl md:text-2xl font-bold text-foreground-900">{stat.value}</div>
                <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hot Comics Section */}
      <section className="py-12 md:py-16 bg-background-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">🔥 热门推荐</h2>
              <p className="text-sm text-foreground-500 mt-1">大家都在看的韩漫精选</p>
            </div>
            <Link to="/category/all?sort=rating" className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer">
              查看全部 <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
            {hotComics.slice(0, 6).map((comic) => (
              <ComicCard key={comic.id} comic={comic} size="medium" />
            ))}
          </div>
        </div>
      </section>

      {/* Category Grid */}
      <section className="py-12 md:py-16 bg-background-100">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">分类浏览</h2>
            <p className="text-sm text-foreground-500 mt-1">发现你喜欢的漫画类型</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
            {categories.filter(c => c.slug !== 'all').map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group relative h-32 md:h-36 rounded-xl overflow-hidden bg-background-200 cursor-pointer"
              >
                <img
                  src={cat.image}
                  alt={`${cat.name}漫画分类`}
                  title={`${cat.name}漫画 - 免费漫画网`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-black/10"></div>
                <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4">
                  <span className="text-background-50 font-semibold text-sm md:text-base">{cat.name}漫画</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Updates */}
      <section className="py-12 md:py-16 bg-background-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">最新更新</h2>
              <p className="text-sm text-foreground-500 mt-1">刚刚更新的漫画章节</p>
            </div>
            <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer">
              更多更新 <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
            {latestComics.map((comic) => (
              <ComicCard key={comic.id} comic={comic} size="medium" />
            ))}
          </div>
        </div>
      </section>

      {/* New Comics Banner */}
      <section className="py-12 md:py-16 bg-accent-900">
        <div className="max-w-6xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-xl md:text-2xl font-bold font-heading text-background-50 mb-3">🆕 新作上架</h2>
          <p className="text-sm text-accent-200 mb-8 max-w-lg mx-auto">
            最新引进的正版韩漫，第一时间为你呈现
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 gap-4 md:gap-6 max-w-3xl mx-auto">
            {newComics.slice(0, 3).map((comic) => (
              <Link key={comic.id} to={`/article/${comic.slug}`} className="group block text-left">
                <div className="w-full aspect-[3/4] overflow-hidden rounded-lg mb-3 bg-background-200">
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title} - 新作韩漫`}
                    title={`${comic.title} - 免费韩漫下拉式阅读`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <h3 className="text-sm font-medium text-background-50 line-clamp-1 group-hover:text-primary-300 transition-colors">
                  {comic.title}
                </h3>
                <p className="text-xs text-accent-300 mt-1">{comic.author}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-16 bg-background-100">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 text-center mb-8">常见问题</h2>
          <div className="space-y-3">
            {[
              { q: '免费漫画网真的是免费的吗？', a: '是的，免费漫画网提供正版韩漫的免费在线阅读服务。我们采用广告支持的商业模式，让用户在享受高质量漫画的同时无需付费。' },
              { q: '什么是韩漫下拉式阅读？', a: '韩漫下拉式阅读是一种专为手机优化的漫画阅读方式，画面纵向排列，读者只需向下滑动即可连续阅读，不需要翻页，体验非常流畅。' },
              { q: '漫画内容是否合法正版？', a: '免费漫画网所有漫画内容均获得正版授权，与韩国各大漫画平台和出版社建立了长期合作关系，确保每一部作品都合法合规。' },
              { q: '如何找到我想看的漫画？', a: '您可以通过首页的分类导航浏览不同类型的漫画，也可以使用搜索功能直接查找作品名称，或者查看热门排行榜发现大家都在看的好漫画。' },
            ].map((faq, index) => (
              <details key={index} className="group bg-background-50 rounded-lg border border-background-200/70 cursor-pointer">
                <summary className="px-5 py-4 text-sm font-medium text-foreground-800 list-none flex items-center justify-between">
                  {faq.q}
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </div>
                </summary>
                <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Schema.org FAQPage JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          { "@type": "Question", "name": "免费漫画网真的是免费的吗？", "acceptedAnswer": { "@type": "Answer", "text": "是的，免费漫画网提供正版韩漫的免费在线阅读服务。我们采用广告支持的商业模式，让用户在享受高质量漫画的同时无需付费。" } },
          { "@type": "Question", "name": "什么是韩漫下拉式阅读？", "acceptedAnswer": { "@type": "Answer", "text": "韩漫下拉式阅读是一种专为手机优化的漫画阅读方式，画面纵向排列，读者只需向下滑动即可连续阅读，不需要翻页，体验非常流畅。" } },
          { "@type": "Question", "name": "漫画内容是否合法正版？", "acceptedAnswer": { "@type": "Answer", "text": "免费漫画网所有漫画内容均获得正版授权，与韩国各大漫画平台和出版社建立了长期合作关系，确保每一部作品都合法合规。" } },
          { "@type": "Question", "name": "如何找到我想看的漫画？", "acceptedAnswer": { "@type": "Answer", "text": "您可以通过首页的分类导航浏览不同类型的漫画，也可以使用搜索功能直接查找作品名称，或者查看热门排行榜发现大家都在看的好漫画。" } },
        ],
      }) }} />
    </main>
  );
}